/**
 * ReservationsPage — the reservation pipeline (lead → offer lifecycle).
 *
 * Route: `reservations` · RBAC: `reservation:read` · Nav group: Hospitality.
 *
 * Renders the pipeline board grouped by canonical status over the current
 * server-filtered/paginated window, with a toolbar (status/urgency/language +
 * arrival range + search) bound to URL search params and a Pagination footer.
 * A detail Sheet handles transitions + the offer lifecycle. All data flows
 * through hooks; resolved tenant/hotel UUIDs come from useHotelContext.
 */

import { type ReservationsFilters, useReservations } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  EmptyState,
  ErrorState,
  ListToolbar,
  LoadingState,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hostesa/ui';
import { CalendarCheck } from 'lucide-react';
import { useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import type { Reservation, ReservationStatus } from './_deps';
import { namespace, resources } from './i18n';
import { ReservationDetailPanel } from './reservation-detail-panel';
import { ReservationPipeline } from './reservation-pipeline';

/** Exposed for tests: register this slice's strings via renderWithProviders. */
export const reservationsI18nResources = {
  en: { [namespace]: resources.en },
  bg: { [namespace]: resources.bg },
  de: { [namespace]: resources.de },
  fr: { [namespace]: resources.fr },
} as const;

const STATUS_OPTIONS: ReservationStatus[] = [
  'new',
  'missing_information',
  'offer_needed',
  'offer_drafted',
  'offer_sent',
  'waiting_for_guest',
  'converted',
  'lost',
  'cancelled',
];

export interface ReservationsPageProps {
  /** URL-driven server filter/search/pagination state. */
  filters?: ReservationsFilters;
  /** Apply a new filter set (the route writes it to URL search params). */
  onFiltersChange?: (next: ReservationsFilters) => void;
}

export function ReservationsPage({ filters = {}, onFiltersChange }: ReservationsPageProps = {}) {
  const { t } = useT(namespace);
  const { tenantId, hotelId } = useHotelContext();

  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;
  const reservationsQuery = useReservations(tenantId, hotelId, { ...filters, limit, offset });

  const reservations = reservationsQuery.data?.items ?? [];
  const total = reservationsQuery.data?.total ?? 0;

  const filterActive = Boolean(
    filters.status ||
      filters.urgency ||
      filters.contactLanguage ||
      filters.q ||
      filters.from ||
      filters.to,
  );
  const patch = (next: Partial<ReservationsFilters>) => apply({ ...filters, ...next, offset: 0 });

  const [selected, setSelected] = useState<Reservation | null>(null);
  const [open, setOpen] = useState(false);

  function openDetail(reservation: Reservation) {
    setSelected(reservation);
    setOpen(true);
  }

  // Show the LIVE reservation in the detail panel: after a transition / offer
  // action the list query refetches, so re-read the selected lead from fresh
  // data (falling back to the captured snapshot until the refetch lands).
  const liveSelected = selected
    ? (reservations.find((r) => r.id === selected.id) ?? selected)
    : null;

  return (
    <div data-testid="reservations-page" className="flex flex-col">
      <PageHeader
        data-testid="reservations-page-header"
        eyebrow={t('eyebrow')}
        title={t('title')}
        description={t('description')}
      />

      <ListToolbar
        data-testid="reservations-toolbar"
        searchValue={filters.q ?? ''}
        onSearchChange={(v) => patch({ q: v || undefined })}
        searchPlaceholder={t('toolbar.searchPlaceholder')}
        trailing={
          filterActive ? (
            <Button
              data-testid="reservations-clear-filters"
              variant="ghost"
              size="sm"
              onClick={() => apply({ limit })}
            >
              {t('toolbar.clear')}
            </Button>
          ) : undefined
        }
      >
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) =>
            patch({ status: v === 'all' ? undefined : (v as ReservationStatus) })
          }
        >
          <SelectTrigger data-testid="reservations-filter-status" className="h-8 w-48">
            <SelectValue placeholder={t('toolbar.statusAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('toolbar.statusAll')}</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`reservations-filter-status-${s}`}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.urgency ?? 'all'}
          onValueChange={(v) =>
            patch({ urgency: v === 'all' ? undefined : (v as 'low' | 'medium' | 'high') })
          }
        >
          <SelectTrigger data-testid="reservations-filter-urgency" className="h-8 w-40">
            <SelectValue placeholder={t('toolbar.urgencyAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('toolbar.urgencyAll')}</SelectItem>
            <SelectItem value="low">{t('urgency.low')}</SelectItem>
            <SelectItem value="medium">{t('urgency.medium')}</SelectItem>
            <SelectItem value="high">{t('urgency.high')}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <input
            type="date"
            aria-label={t('toolbar.fromLabel')}
            data-testid="reservations-from"
            value={filters.from ?? ''}
            onChange={(e) => patch({ from: e.target.value || undefined })}
            className="h-8 w-40 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-2 text-sm text-[var(--color-charcoal)]"
          />
          <input
            type="date"
            aria-label={t('toolbar.toLabel')}
            data-testid="reservations-to"
            value={filters.to ?? ''}
            onChange={(e) => patch({ to: e.target.value || undefined })}
            className="h-8 w-40 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-2 text-sm text-[var(--color-charcoal)]"
          />
        </div>
      </ListToolbar>

      {reservationsQuery.isLoading ? (
        <LoadingState data-testid="reservations-loading" title={t('loading')} />
      ) : reservationsQuery.isError ? (
        <ErrorState
          data-testid="reservations-error"
          title={t('error.title')}
          description={t('error.description')}
          action={
            <Button
              data-testid="reservations-retry-btn"
              variant="secondary"
              size="sm"
              onClick={() => void reservationsQuery.refetch()}
            >
              {t('error.retry')}
            </Button>
          }
        />
      ) : reservations.length === 0 ? (
        filterActive ? (
          <EmptyState
            data-testid="reservations-empty-filtered"
            icon={<CalendarCheck />}
            title={t('emptyFiltered.title')}
            description={t('emptyFiltered.description')}
            action={
              <Button
                data-testid="reservations-clear-filters-empty"
                variant="secondary"
                size="sm"
                onClick={() => apply({ limit })}
              >
                {t('toolbar.clear')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            data-testid="reservations-empty"
            icon={<CalendarCheck />}
            title={t('empty.title')}
            description={t('empty.description')}
          />
        )
      ) : (
        <ReservationPipeline reservations={reservations} onOpen={openDetail} />
      )}

      {!reservationsQuery.isLoading && !reservationsQuery.isError && total > 0 && (
        <Pagination
          data-testid="reservations-pagination"
          total={total}
          limit={limit}
          offset={offset}
          onChange={({ offset: nextOffset, limit: nextLimit }) =>
            apply({ ...filters, offset: nextOffset, limit: nextLimit })
          }
          rangeLabel={(from, to, t1) => t('paging.range', { from, to, total: t1 })}
          prevLabel={t('paging.prev')}
          nextLabel={t('paging.next')}
          pageSizeLabel={t('paging.perPage')}
        />
      )}

      <ReservationDetailPanel
        reservation={liveSelected}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSelected(null);
        }}
      />
    </div>
  );
}
